import { useEffect, useState } from 'react'
import { Download, FileText } from 'lucide-react'
import { downloadCourseAttachment, getCourseAttachments, type CourseAttachment } from '@/services/attachmentService'

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`

// เอกสารประกอบการเรียนของคอร์ส (แนบที่ระดับคอร์ส ไม่แยกตามบทเรียน) — หน้าเต็มที่เปิดจากแท็บใน sidebar
export default function CourseDocuments({ courseId }: { courseId: number }) {
  const [items, setItems] = useState<CourseAttachment[] | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    getCourseAttachments(courseId).then((list) => {
      if (!cancelled) setItems(list)
    })
    return () => {
      cancelled = true
    }
  }, [courseId])

  const download = async (item: CourseAttachment) => {
    setBusyId(item.id)
    setError('')
    try {
      await downloadCourseAttachment(courseId, item)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ดาวน์โหลดไม่สำเร็จ')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="flex-1 overflow-auto bg-white p-6 md:p-10">
      <div className="mx-auto" style={{ maxWidth: 760 }}>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent/70">Course Documents</p>
        <h1 className="mb-6 text-2xl font-bold text-[#19172A]">เอกสารประกอบการเรียน</h1>
        {items === null ? (
          <p className="text-sm text-[#69738A]">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#69738A]">ยังไม่มีเอกสาร</p>
        ) : (
          <ul className="divide-y divide-[#EEECF6] border border-[#E7E5F0]">
            {items.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <span className="flex min-w-0 items-center gap-3 text-sm text-[#19172A]">
                  <FileText className="h-4 w-4 shrink-0 text-accent" />
                  <span className="min-w-0 truncate">{item.name}</span>
                  <span className="shrink-0 text-xs text-[#69738A]">{formatSize(item.size)}</span>
                </span>
                <button
                  onClick={() => download(item)}
                  disabled={busyId === item.id}
                  className="flex shrink-0 items-center gap-1.5 rounded border border-[#E7E5F0] bg-white px-3 py-1.5 text-xs text-[#19172A] transition hover:border-accent/40 hover:text-accent disabled:opacity-50"
                >
                  <Download className="h-3.5 w-3.5" /> {busyId === item.id ? 'กำลังโหลด...' : 'ดาวน์โหลด'}
                </button>
              </li>
            ))}
          </ul>
        )}
        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      </div>
    </div>
  )
}
